import { Box, Button, CircularProgress, Divider, Grid, IconButton, MenuItem, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { getCategories } from "api/services/categories";
import { getPreferedServices } from "api/services/client";
import DialogWrapper from "components/DialogWrapper";
import SearchContainer from "components/SearchContainer";
import { StyledServiceDesc, StyledServiceItem, StyledServicesContainer } from "pages/billing/styles";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useLocation } from "react-router-dom";
import { ResType } from "types";

function SelectService({
  open,
  setOpen,
  formState,
  setFormState,
}) {
  const location: any = useLocation();
  const { categoryId, subCategoryId } = location.state || {};

  const [filters, setFilters] = useState<any>({
    category: categoryId || null,
    subCategory: subCategoryId || null,
  });

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 12;
  const offset = (page - 1) * limit;

  const [data, setData] = useState<any>([]);
  const [clicked, setClicked] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const { isLoading }: ResType = useQuery(
    ["services", { search, limit, offset, ...filters }],
    getPreferedServices,
    {
      enabled: open,
      onSuccess: (res: any) => {
        if (clicked) {
          setData((prev: any[]) => [...prev, ...res.data.result]);
          setClicked(false);
        } else {
          setData(res.data.result);
        }
      },
    }
  );

  const { data: categories }: ResType = useQuery(
    "categories",
    getCategories,
    { enabled: open }
  );

  const subCategories = categories?.data?.find(
    (item: any) => item.id === +filters.category
  )?.subCategories;

  const handleCategoryChange = (e: any) => {
    setFilters({
      category: e.target.value,
      subCategory: null,
    });
    setPage(1);
  };

  const handleSubCategoryChange = (e: any) => {
    setFilters({
      ...filters,
      subCategory: e.target.value,
    });
    setPage(1);
  };

  const handleServiceSelect = (service: any) => {
    setFormState((prev: any) => ({
      ...prev,
      service,
    }));
    setOpen(false);
  };

  return (
    <DialogWrapper width="lg" open={open} setOpen={setOpen} title="Select Service">
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={isSmallScreen ? 1 : 2}
          alignItems="center"
        >
          <TextField
            label="Service Category"
            value={filters.category}
            sx={{
              minWidth: 150,
              width: isSmallScreen ? "100%" : 200,
              mb: isSmallScreen ? 1 : 0,
            }}
            onChange={handleCategoryChange}
            size="small"
            select
          >
            <MenuItem value={"0"}>Select</MenuItem>
            {categories?.data?.map((option: any, index: number) => (
              <MenuItem key={index} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>

          {subCategories?.length > 0 && (
            <TextField
              sx={{
                minWidth: 150,
                width: isSmallScreen ? "100%" : 200,
                mb: isSmallScreen ? 1 : 0,
              }}
              value={filters.subCategory}
              label="Service Sub-Category"
              onChange={handleSubCategoryChange}
              size="small"
              select
            >
              {subCategories.map((option: any, index: number) => (
                <MenuItem key={index} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>

        <SearchContainer
          minWidth={isSmallScreen ? "260px" : "400px"}
          placeHolder="Search by Categories | Service | Label"
          value={search}
          onChange={(v: string) => {
            setSearch(v);
            setPage(1);
          }}
          debounced
        />
      </Box>

      <Divider sx={{ mt: 2 }} />

      <StyledServicesContainer>
        <Grid container spacing={2}>
          {/* ================= ALL SERVICES ================= */}
          <Grid item container spacing={2} xs={12}>
            {data.map((item: any, index: number) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <StyledServiceItem onClick={() => handleServiceSelect(item)}>
                  <Box width="100%">
                    <Typography variant="caption" color="rgba(0,0,0,0.6)">
                      {item?.category?.name}
                      {item?.subCategory && ` || ${item?.subCategory?.name}`}
                    </Typography>

                    <StyledServiceDesc>
                      <Typography variant="subtitle2">
                        {item?.name}
                      </Typography>
                    </StyledServiceDesc>

                    <Typography variant="body2" color="rgba(0,0,0,0.6)">
                      <StyledServiceDesc
                        dangerouslySetInnerHTML={{
                          __html: item?.description,
                        }}
                      />
                    </Typography>
                  </Box>
                </StyledServiceItem>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Box sx={{ textAlign: "center", pt: 2 }}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            data.length > 0 && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setClicked(true);
                  setPage((p) => p + 1);
                }}
              >
                Load More
              </Button>
            )
          )}
        </Box>
      </StyledServicesContainer>
    </DialogWrapper>
  );
}

export default SelectService;
